import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { storyTemplateFormMessages } from './storyTemplateForm.messages';
import { sharedMessages } from '@/shared-messages/shared.messages';
import {
  CreateStoryTemplateRequestSchema,
  StoryThemesSchema,
  AgeRangeSchema,
} from 'schemas';

import type { RoleTemplate, StyleTemplateWithUrls } from 'schemas';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Heading3, Small } from '@/components/Typography';
import { useState } from 'react';
import { fetchRoleTemplates } from '@/api/services/role-templates/fetchRoleTemplates';
import { fetchStyleTemplates } from '@/api/services/style-templates/fetchStyleTemplates';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { toast } from 'sonner';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';

interface StoryTemplateFormProps {
  onSubmit: (formData: CreateStoryTemplateFormValues) => Promise<void>;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

type CreateStoryTemplateFormValues = z.infer<
  typeof CreateStoryTemplateRequestSchema
>;

// Get all theme options from the enum with direct message mapping
const storyThemeOptions = Object.values(StoryThemesSchema.enum).map(
  (theme) => ({
    value: theme,
    label: sharedMessages.storyThemeOptions[theme],
  })
);

// Get all age range options from the enum with direct message mapping
const ageRangeOptions = Object.values(AgeRangeSchema.enum).map((ageRange) => ({
  value: ageRange,
  label: storyTemplateFormMessages.ageRangeOptions[ageRange],
}));

export function StoryTemplateForm({
  onSubmit,
  isSubmitting = false,
  onCancel,
}: StoryTemplateFormProps) {
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [loadingRoleTemplates, setLoadingRoleTemplates] = useState(true);
  const [styleTemplates, setStyleTemplates] = useState<StyleTemplateWithUrls[]>(
    []
  );
  const [loadingStyleTemplates, setLoadingStyleTemplates] = useState(true);

  const form = useForm<CreateStoryTemplateFormValues>({
    resolver: zodResolver(CreateStoryTemplateRequestSchema),
    defaultValues: {
      title: '',
      description: '',
      ageRange: '3-6',
      storyThemes: ['family'],
      keyEvents: [],
      settingDetails: '',
      style_template_id: '',
      role_template_ids: [''],
    },
  });

  // Load role templates on mount
  useLoadOnce(() => {
    const loadRoleTemplates = async () => {
      try {
        setLoadingRoleTemplates(true);
        const templates = await fetchRoleTemplates();
        setRoleTemplates(templates);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoadingRoleTemplates(false);
      }
    };

    loadRoleTemplates();
  });

  // Load style templates on mount
  useLoadOnce(() => {
    const loadStyleTemplates = async () => {
      try {
        setLoadingStyleTemplates(true);
        const templates = await fetchStyleTemplates();
        setStyleTemplates(templates);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoadingStyleTemplates(false);
      }
    };

    loadStyleTemplates();
  });

  const handleFormSubmit = async (data: CreateStoryTemplateFormValues) => {
    await onSubmit(data);
  };

  const addRoleTemplate = () => {
    const currentIds = form.getValues('role_template_ids');
    form.setValue('role_template_ids', [...currentIds, '']);
  };

  const removeRoleTemplate = (index: number) => {
    const currentIds = form.getValues('role_template_ids');
    const removedId = currentIds[index];
    const newIds = currentIds.filter((_, i) => i !== index);
    form.setValue('role_template_ids', newIds);

    if (removedId && removedId.trim() !== '') {
      form.trigger('role_template_ids');
    }
  };

  const addKeyEvent = () => {
    const currentEvents = form.getValues('keyEvents') || [];
    form.setValue('keyEvents', [...currentEvents, '']);
  };

  const removeKeyEvent = (index: number) => {
    const currentEvents = form.getValues('keyEvents') || [];
    const newEvents = currentEvents.filter((_, i) => i !== index);
    form.setValue('keyEvents', newEvents);
  };

  const selectedRoleTemplateIds = form.watch('role_template_ids');
  const selectedKeyEvents = form.watch('keyEvents') || [];

  return (
    <div className="mx-auto w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="space-y-6"
          aria-label={storyTemplateFormMessages.formAriaLabel}
        >
          {/* Title Field */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="mb-2">
                  {storyTemplateFormMessages.titleLabel}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={storyTemplateFormMessages.titlePlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description Field */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="mb-2">
                  {storyTemplateFormMessages.descriptionLabel}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      storyTemplateFormMessages.descriptionPlaceholder
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Age Range Field */}
          <FormField
            control={form.control}
            name="ageRange"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{storyTemplateFormMessages.ageRangeLabel}</FormLabel>
                <Small className="mb-2">
                  {storyTemplateFormMessages.ageRangeDescription}
                </Small>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          storyTemplateFormMessages.ageRangePlaceholder
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ageRangeOptions.map((ageRange) => (
                      <SelectItem key={ageRange.value} value={ageRange.value}>
                        {ageRange.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Story Themes Field */}
          <FormField
            control={form.control}
            name="storyThemes"
            render={() => (
              <FormItem>
                <FormLabel>
                  {storyTemplateFormMessages.storyThemesLabel}
                </FormLabel>
                <Small className="mb-2">
                  {storyTemplateFormMessages.storyThemesDescription}
                </Small>
                <div className="grid grid-cols-2 gap-4">
                  {storyThemeOptions.map((theme) => (
                    <FormField
                      key={theme.value}
                      control={form.control}
                      name="storyThemes"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={theme.value}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(theme.value)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([
                                        ...field.value,
                                        theme.value,
                                      ])
                                    : field.onChange(
                                        field.value?.filter(
                                          (value) => value !== theme.value
                                        )
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel>{theme.label}</FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Key Events Section */}
          <div className="space-y-4">
            <div>
              <FormLabel className="mb-1">
                {storyTemplateFormMessages.keyEventsLabel}
              </FormLabel>
              <Small className="mb-2">
                {storyTemplateFormMessages.keyEventsDescription}
              </Small>
            </div>

            {selectedKeyEvents.map((_, index) => (
              <div key={index} className="space-y-4 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <Heading3>Key Event {index + 1}</Heading3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeKeyEvent(index)}
                    aria-label={
                      storyTemplateFormMessages.removeKeyEventAriaLabel
                    }
                  >
                    {storyTemplateFormMessages.removeKeyEventButton}
                  </Button>
                </div>

                <FormField
                  control={form.control}
                  name={`keyEvents.${index}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder={
                            storyTemplateFormMessages.keyEventsPlaceholder
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addKeyEvent}
              className="w-full"
              aria-label={storyTemplateFormMessages.addKeyEventAriaLabel}
            >
              {storyTemplateFormMessages.addKeyEventButton}
            </Button>
          </div>

          {/* Setting Details Field */}
          <FormField
            control={form.control}
            name="settingDetails"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {storyTemplateFormMessages.settingDetailsLabel}
                </FormLabel>
                <Small className="mb-2">
                  {storyTemplateFormMessages.settingDetailsDescription}
                </Small>
                <FormControl>
                  <Textarea
                    placeholder={
                      storyTemplateFormMessages.settingDetailsPlaceholder
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Role Templates Section */}
          <div className="space-y-4">
            <div>
              <Heading3 className="mb-1">
                {storyTemplateFormMessages.rolesLabel}
              </Heading3>
              <Small className="mb-2">
                {storyTemplateFormMessages.rolesDescription}
              </Small>
            </div>

            {loadingRoleTemplates ? (
              <div className="py-4 text-center">
                <Small>{storyTemplateFormMessages.loadingRoleTemplates}</Small>
              </div>
            ) : (
              <>
                {selectedRoleTemplateIds.map((_, index) => (
                  <div key={index} className="space-y-4 rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <Heading3>Role {index + 1}</Heading3>
                      {selectedRoleTemplateIds.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeRoleTemplate(index)}
                          aria-label={
                            storyTemplateFormMessages.removeRoleAriaLabel
                          }
                        >
                          {storyTemplateFormMessages.removeRoleButton}
                        </Button>
                      )}
                    </div>

                    <FormField
                      control={form.control}
                      name={`role_template_ids.${index}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {storyTemplateFormMessages.roleTemplateLabel}
                          </FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={
                                    storyTemplateFormMessages.roleTemplatePlaceholder
                                  }
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {roleTemplates.map((template) => (
                                <SelectItem
                                  key={template.id}
                                  value={template.id}
                                >
                                  <div>
                                    <div className="font-medium">
                                      {template.title}
                                    </div>
                                    <div className="text-muted-foreground text-sm">
                                      {template.description}
                                    </div>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addRoleTemplate}
                  className="w-full"
                  aria-label={storyTemplateFormMessages.addRoleAriaLabel}
                >
                  {storyTemplateFormMessages.addRoleButton}
                </Button>
              </>
            )}
          </div>

          {/* Style Template Field */}
          <FormField
            control={form.control}
            name="style_template_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {storyTemplateFormMessages.styleTemplateLabel}
                </FormLabel>
                <Small className="mb-2">
                  {storyTemplateFormMessages.styleTemplateDescription}
                </Small>
                {loadingStyleTemplates ? (
                  <div className="py-4 text-center">
                    <Small>
                      {storyTemplateFormMessages.loadingStyleTemplates}
                    </Small>
                  </div>
                ) : (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            storyTemplateFormMessages.styleTemplatePlaceholder
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {styleTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Form Actions */}
          <div className="flex gap-4 pt-6">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {storyTemplateFormMessages.cancelButton}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting || !form.formState.isValid}
              className="flex-1"
            >
              {isSubmitting
                ? storyTemplateFormMessages.creating
                : storyTemplateFormMessages.createTemplateButton}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
